/**
 * useDebouncedAutosave.js — Employer v1.1 Phase 0.6 Slice B.
 *
 * Per pre-code plan §13.2 G-D: 1500 ms debounce.
 *
 * Usage:
 *   const { trigger, status, lastSavedAt, error, flush } =
 *     useDebouncedAutosave(saveFn, { delayMs: 1500 });
 *   trigger(value);   // schedules a save
 *   await flush();    // run any pending save now
 *
 * Status values: 'idle' | 'pending' | 'saving' | 'saved' | 'error'.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const AUTOSAVE_DEBOUNCE_MS = 1500;

export function useDebouncedAutosave(saveFn, { delayMs = AUTOSAVE_DEBOUNCE_MS } = {}) {
  const [status,      setStatus]      = useState('idle');
  const [error,       setError]       = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const timerRef   = useRef(null);
  const pendingRef = useRef(null);
  const inFlight   = useRef(false);
  const fnRef      = useRef(saveFn);

  useEffect(() => { fnRef.current = saveFn; }, [saveFn]);

  const runSave = useCallback(async () => {
    if (inFlight.current) return;
    const payload = pendingRef.current;
    if (payload === null || payload === undefined) return;
    pendingRef.current = null;
    inFlight.current   = true;
    setStatus('saving');
    setError(null);
    try {
      await fnRef.current(payload);
      setLastSavedAt(new Date());
      setStatus('saved');
    } catch (e) {
      setError(e?.message || 'Save failed');
      setStatus('error');
    } finally {
      inFlight.current = false;
      // If another payload was queued while we were saving, schedule again.
      if (pendingRef.current !== null) {
        setStatus('pending');
        timerRef.current = setTimeout(runSave, delayMs);
      }
    }
  }, [delayMs]);

  const trigger = useCallback((payload) => {
    pendingRef.current = payload;
    setStatus('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(runSave, delayMs);
  }, [runSave, delayMs]);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await runSave();
  }, [runSave]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { trigger, flush, status, error, lastSavedAt };
}
